import { createFileRoute } from "@tanstack/react-router";
import { Building2, Target, Users, Heart } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/about")({
  component: About,
});

function About() {
  useEffect(() => {
    document.title = "About Us — 24Boda";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="/" className="flex items-center">
            <img src="/logo.png" alt="24Boda" className="h-10 w-auto" />
          </a>
          <nav className="hidden items-center gap-8 text-sm font-medium text-muted-foreground md:flex">
            <a href="/" className="transition-colors hover:text-foreground">
              Home
            </a>
            <a href="/about" className="text-foreground">
              About
            </a>
            <a href="/blog" className="transition-colors hover:text-foreground">
              Blog
            </a>
            <a href="/faq" className="transition-colors hover:text-foreground">
              FAQ
            </a>
            <a href="/join-riders" className="transition-colors hover:text-foreground">
              Riders
            </a>
          </nav>
          <a
            href="/#download"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Download App
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Transforming delivery in Kampala
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              24Boda is on a mission to make boda boda delivery fast, safe, and reliable for
              everyone in Uganda.
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
              <ValueCard
                icon={Target}
                title="Our Mission"
                description="Make delivery accessible and affordable for every Ugandan through technology and trust."
              />
              <ValueCard
                icon={Users}
                title="Our Community"
                description="Empowering riders with fair earnings while providing customers with reliable service."
              />
              <ValueCard
                icon={Heart}
                title="Our Values"
                description="Safety first, transparency always, and respect for everyone we serve."
              />
              <ValueCard
                icon={Building2}
                title="Our Future"
                description="Expanding across Uganda to connect more communities with better logistics."
              />
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="border-y border-border bg-secondary/30 py-16 sm:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight text-foreground">Our Story</h2>
            <div className="mt-8 space-y-4 text-muted-foreground">
              <p>
                24Boda was born from a simple observation: Kampala's traffic is chaotic, and
                traditional delivery methods are slow and unreliable. But boda bodas can navigate
                through anything.
              </p>
              <p>
                We built a platform that connects verified riders with customers who need fast,
                affordable delivery. No more haggling, no more guesswork — just transparent pricing
                and real-time tracking.
              </p>
              <p>
                Today, we're proud to serve thousands of customers and provide earning opportunities
                for hundreds of riders across Kampala. But we're just getting started.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex items-center">
              <img src="/logo.png" alt="24Boda" className="h-8 w-auto" />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              © {new Date().getFullYear()} 24Boda. All rights reserved. Kampala, Uganda.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ValueCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-6" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
